import { useEffect, useMemo, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Search, X, Flame, Clock, ServerCrash, Filter } from "lucide-react";
import { toast } from "sonner";
import { ResultsTable } from "@/components/ResultsTable";
import { ProviderPanel } from "@/components/ProviderPanel";
import { DetailsPanel } from "@/components/DetailsPanel";
import { fetchProviders, searchTorrents, browseTorrents } from "@/lib/api";

const CATEGORIES = ["All", "Movies", "Series", "Anime", "Games", "Apps", "Books", "Music", "Porn", "Other"];
const MODES = [
  { key: "search", label: "Search", icon: Search },
  { key: "top", label: "Top", icon: Flame },
  { key: "latest", label: "Latest", icon: Clock },
];

export default function SearchApp() {
  const [providers, setProviders] = useState([]);
  const [enabled, setEnabled] = useState(new Set());
  const [category, setCategory] = useState("All");
  const [mode, setMode] = useState("search");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hideDead, setHideDead] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selected, setSelected] = useState(null);

  const detailsProviders = useMemo(
    () => new Set(providers.filter((p) => p.hasDetails).map((p) => p.name)),
    [providers]
  );

  useEffect(() => {
    fetchProviders()
      .then((ps) => {
        setProviders(ps);
        setEnabled(new Set(ps.filter((p) => p.enabledByDefault).map((p) => p.id)));
      })
      .catch(() => toast.error("Failed to load providers"));
  }, []);

  const enabledIds = useMemo(() => [...enabled], [enabled]);

  const runSearch = useCallback(
    async (q, cat, ids) => {
      if (!q.trim()) return;
      setLoading(true);
      setHasSearched(true);
      try {
        const data = await searchTorrents({ query: q.trim(), category: cat, providerIds: ids });
        setResults(data.results);
        setErrors(data.errors || []);
      } catch (e) {
        toast.error("Search failed", { description: e.message });
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const runBrowse = useCallback(async (type, cat, ids) => {
    setLoading(true);
    setHasSearched(true);
    try {
      const data = await browseTorrents({ type, category: cat, providerIds: ids });
      setResults(data.results);
      setErrors(data.errors || []);
    } catch (e) {
      toast.error("Browse failed", { description: e.message });
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const onSubmit = (e) => {
    e.preventDefault();
    runSearch(query, category, enabledIds);
  };

  const onModeChange = (m) => {
    setMode(m);
    if (m === "search") {
      if (query.trim()) runSearch(query, category, enabledIds);
    } else {
      runBrowse(m, category, enabledIds);
    }
  };

  const onCategoryChange = (cat) => {
    setCategory(cat);
    if (mode === "search") {
      if (query.trim()) runSearch(query, cat, enabledIds);
    } else {
      runBrowse(mode, cat, enabledIds);
    }
  };

  const refetch = () => {
    if (mode === "search") {
      if (query.trim()) runSearch(query, category, enabledIds);
    } else {
      runBrowse(mode, category, enabledIds);
    }
  };

  const toggleProvider = (id) => {
    setEnabled((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleAll = (on) => {
    setEnabled(on ? new Set(providers.map((p) => p.id)) : new Set());
  };

  const visible = useMemo(
    () => (hideDead ? results.filter((t) => !t.isDead) : results),
    [results, hideDead]
  );

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-black/60 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-[1500px] mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-md bg-cyan-500 flex items-center justify-center">
              <Search size={17} className="text-black" strokeWidth={3} />
            </div>
            <div>
              <h1 className="font-mono font-extrabold text-lg tracking-tighter leading-none">
                TORRENT<span className="text-cyan-400">//</span>SEARCH
              </h1>
              <p className="text-[10px] font-mono text-neutral-600 tracking-wider">
                META-SEARCH · {providers.length} PROVIDERS
              </p>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-1 bg-neutral-900/60 border border-neutral-800 rounded-lg p-1">
            {MODES.map((m) => {
              const Icon = m.icon;
              const active = mode === m.key;
              return (
                <button
                  key={m.key}
                  data-testid={`mode-${m.key}`}
                  onClick={() => onModeChange(m.key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-mono uppercase tracking-wider transition-colors duration-150 ${
                    active ? "bg-cyan-500 text-black font-bold" : "text-neutral-400 hover:text-white"
                  }`}
                >
                  <Icon size={13} />
                  {m.label}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto px-4 md:px-8 py-6 md:py-8">
        {/* Search bar */}
        <form onSubmit={onSubmit} className="relative mb-4">
          <Search
            size={20}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none"
          />
          <input
            data-testid="search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={
              mode === "search"
                ? "Search torrents across every provider…"
                : "Switch to Search mode to query by keyword"
            }
            disabled={mode !== "search"}
            className="w-full bg-neutral-950 border border-neutral-800 rounded-xl pl-12 pr-28 py-4 text-base md:text-lg text-white placeholder:text-neutral-600 focus:outline-none focus:border-cyan-400 focus:shadow-[0_0_20px_rgba(0,229,255,0.15)] transition-colors duration-150 disabled:opacity-50"
          />
          {query && (
            <button
              type="button"
              data-testid="clear-search"
              onClick={() => setQuery("")}
              className="absolute right-24 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-white transition-colors"
            >
              <X size={18} />
            </button>
          )}
          <button
            type="submit"
            data-testid="search-submit"
            disabled={mode !== "search"}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-cyan-500 hover:bg-cyan-400 disabled:opacity-40 text-black font-mono font-bold text-sm uppercase tracking-wider px-5 py-2.5 rounded-lg transition-colors duration-150"
          >
            Go
          </button>
        </form>

        {/* Category chips */}
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 mb-6">
          {CATEGORIES.map((cat) => {
            const active = category === cat;
            return (
              <button
                key={cat}
                data-testid={`category-${cat.toLowerCase()}`}
                onClick={() => onCategoryChange(cat)}
                className={`shrink-0 px-4 py-1.5 rounded-full text-xs font-mono uppercase tracking-wider border transition-colors duration-150 ${
                  active
                    ? "border-cyan-400 text-cyan-400 bg-cyan-400/10"
                    : "border-neutral-800 text-neutral-400 hover:border-neutral-600 hover:text-neutral-200"
                }`}
              >
                {cat}
              </button>
            );
          })}
        </div>

        {/* Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Sidebar */}
          <aside className="lg:col-span-3 order-2 lg:order-1">
            <div className="lg:sticky lg:top-24 space-y-4">
              <ProviderPanel
                providers={providers}
                enabled={enabled}
                onToggle={toggleProvider}
                onToggleAll={toggleAll}
                hideDead={hideDead}
                onHideDead={setHideDead}
              />
              <button
                data-testid="apply-refetch"
                onClick={refetch}
                className="w-full flex items-center justify-center gap-2 border border-neutral-800 hover:border-cyan-500/50 hover:text-cyan-300 text-neutral-400 rounded-lg py-2.5 text-xs font-mono uppercase tracking-wider transition-colors duration-150"
              >
                <Filter size={13} /> Apply provider selection
              </button>
            </div>
          </aside>

          {/* Results */}
          <section className="lg:col-span-9 order-1 lg:order-2">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-mono text-neutral-500">
                {loading ? (
                  <span className="blink-cursor text-cyan-400">querying providers</span>
                ) : hasSearched ? (
                  <span>
                    <span className="text-neutral-200 font-bold">{visible.length}</span> results
                    {hideDead && results.length !== visible.length && (
                      <span className="text-neutral-600"> · {results.length - visible.length} dead hidden</span>
                    )}
                  </span>
                ) : (
                  <span className="text-neutral-600">Ready.</span>
                )}
              </div>
            </div>

            {errors.length > 0 && !loading && (
              <div className="mb-3 flex flex-wrap gap-2">
                {errors.map((e) => (
                  <span
                    key={e.provider}
                    className="inline-flex items-center gap-1 text-[10px] font-mono px-2 py-1 rounded bg-rose-500/10 text-rose-400 border border-rose-500/20"
                  >
                    <ServerCrash size={11} /> {e.provider} unreachable
                  </span>
                ))}
              </div>
            )}

            {loading ? (
              <LoadingState />
            ) : !hasSearched ? (
              <EmptyState />
            ) : visible.length === 0 ? (
              <NoResults />
            ) : (
              <ResultsTable
                torrents={visible}
                onSelect={setSelected}
                detailsProviders={detailsProviders}
              />
            )}
          </section>
        </div>

        <footer className="mt-14 pt-6 border-t border-neutral-900 text-center">
          <p className="text-[11px] font-mono text-neutral-700 max-w-2xl mx-auto leading-relaxed">
            Ported from prajwalch/TorrentSearch. This tool does not host or store any files — it
            queries publicly accessible third-party sources and displays their results. You are
            responsible for complying with your local laws.
          </p>
        </footer>
      </main>

      <DetailsPanel
        torrent={selected}
        hasDetails={selected ? detailsProviders.has(selected.providerName) : false}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}

function LoadingState() {
  return (
    <div className="rounded-lg border border-neutral-800 bg-neutral-900/40 p-16 flex flex-col items-center justify-center">
      <div className="font-mono text-cyan-400 text-sm blink-cursor">$ fetching torrents</div>
      <p className="text-neutral-600 text-xs mt-3 font-mono">running queries in parallel…</p>
    </div>
  );
}

function EmptyState() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="rounded-lg border border-dashed border-neutral-800 bg-neutral-900/20 p-16 text-center"
    >
      <Search size={36} className="mx-auto text-neutral-700 mb-4" />
      <p className="text-neutral-400 text-sm">Type a query and hit Go, or switch to Top / Latest.</p>
      <p className="text-neutral-600 text-xs mt-2 font-mono">
        Try: "ubuntu", "big buck bunny", "blender demo"
      </p>
    </motion.div>
  );
}

function NoResults() {
  return (
    <div className="rounded-lg border border-neutral-800 bg-neutral-900/40 p-16 text-center">
      <p className="text-neutral-400 text-sm">No torrents found.</p>
      <p className="text-neutral-600 text-xs mt-2 font-mono">
        Try a different query, category, or enable more providers.
      </p>
    </div>
  );
}
